import { BLOG_ARTICLES } from "@config/blog-config";
import zazeh from "@public/static/landing-pages/zazeh.jpg";
import { BlogHeroSection } from "@shared/components/blog/blog-hero-section";
import { IntroSection } from "@shared/components/blog/intro-section";
import { MainSection } from "@shared/components/blog/main-section";
import { Sidebar } from "@shared/components/blog/sidebar";
import { useLocale } from "@translations/locale-context";
import { StaticImage } from "@ui/components/image/static-image";
import { TextLink } from "@ui/components/text-link/text-link";
import { useTranslation } from "next-i18next";
import React, { FC, memo } from "react";

const ContentCs: FC = () => (
    <>
        <h2 className="ui-typo-medium mb-12 italic">
            &bdquo;Když je něco tabu, tak to nechci hloupě přijmout a&nbsp;říct si, že to nějakým způsobem dává
            smysl&ldquo;
        </h2>
        <p className="ui-typo-medium mb-12">
            Zaměstnávání přátel a&nbsp;rodiny v&nbsp;profesním světě - ano či ne? Pokud ano, jak oddělit osobní
            a&nbsp;pracovní život? Jaké jsou výhody sportovního ducha v&nbsp;podnikání? O&nbsp;tom všem
            se&nbsp;rozmluvil náš CEO Martin Tryzna pro posluchače podcastu&nbsp;
            <TextLink href="https://www.martinhurych.com/" target="_blank" variant="orange" underline>
                Zážeh Martina Hurycha
            </TextLink>
        </p>
        <p className="ui-typo-medium mb-12">V&nbsp;podcastu se&nbsp;mimo jiné rozpovídal, o&nbsp;tom:</p>
        <ol className="ui-typo-medium mb-8">
            <li className="mb-2 ml-6 list-disc">Co Martina vedlo k zaměstnávání kamarádů a&nbsp;rodiny</li>
            <li className="mb-2 ml-6 list-disc">Jak oddělují pracovní a&nbsp;osobní život</li>
            <li className="mb-2 ml-6 list-disc">Jaký je přínos sportovce do&nbsp;podnikání</li>
            <li className="mb-2 ml-6 list-disc">Jak u&nbsp;kamarádů řeší vytýkání, platy a&nbsp;odměny</li>
            <li className="mb-2 ml-6 list-disc">Co dělá startup úspěšným</li>
        </ol>
        <p className="ui-typo-medium mb-12">
            Poslechněte si tuto epizodu a&nbsp;zjistěte, jak se&nbsp;dá budovat úspěšný tým a&nbsp;kultura
            v&nbsp;moderní firmě.
        </p>
        <p className="ui-typo-medium mb-12">
            Celou epizodu, včetně bonusu a&nbsp;odkazů na&nbsp;poslechové platformy, naleznete na&nbsp;
            <TextLink
                href="https://www.martinhurych.com/post/121-martin-tryzna-jak-ne-zamestnavat-kamarady-a-rodinu"
                target="_blank"
                variant="orange"
                underline
            >
                stránkách Martina Hurycha
            </TextLink>
            .
        </p>
        <div className="w-full max-w-[790px]">
            <div className="mt-6 aspect-video">
                <iframe
                    src="https://www.youtube.com/embed/8pE1QtRAl4M"
                    className="size-full rounded-xl md:rounded-3xl"
                    loading="lazy"
                    allowFullScreen
                    title="Zazeh podcast video"
                />
            </div>
        </div>
    </>
);

const ContentEn: FC = () => (
    <>
        <h2 className="ui-typo-medium mb-12 italic">
            &ldquo;When something is taboo, I don&#39;t want to accept it and think that it makes sense in some
            way.&ldquo;
        </h2>
        <p className="ui-typo-medium mb-12">
            Employing friends and family in the professional world - yes or no? If yes, how to separate personal and
            work life? What are the advantages of sportsmanship in business? Our CEO Martin Tryzna discusses all this in
            the newest episode of the Zážeh podcast by Martin Hurych.
        </p>
        <p className="ui-typo-medium mb-12">In the interview, he also spoke about:</p>
        <ol className="ui-typo-medium mb-8">
            <li className="mb-2 ml-6 list-disc">What led Martin to employ friends and family</li>
            <li className="mb-2 ml-6 list-disc">How they separate work and personal life</li>
            <li className="mb-2 ml-6 list-disc">The contribution of an athlete to business</li>
            <li className="mb-2 ml-6 list-disc">How they address criticism, salaries, and rewards among friends</li>
            <li className="mb-2 ml-6 list-disc">What makes a startup successful</li>
        </ol>
        <p className="ui-typo-medium mb-6">
            Listen to this episode to find out how to build a&nbsp;successful team and culture in a&nbsp;modern company.
        </p>
        <p className="ui-typo-medium mb-12">
            The entire episode, including bonuses and links to listening platforms, can be found on the{" "}
            <TextLink
                href="https://www.martinhurych.com/post/121-martin-tryzna-jak-ne-zamestnavat-kamarady-a-rodinu"
                target="_blank"
                variant="orange"
                underline
            >
                website of Martin Hurych.
            </TextLink>
            .
        </p>
        <div className="w-full max-w-[790px]">
            <div className="mt-6 aspect-video">
                <iframe
                    src="https://www.youtube.com/embed/8pE1QtRAl4M"
                    className="size-full rounded-xl md:rounded-3xl"
                    loading="lazy"
                    allowFullScreen
                    title="Zazeh podcast video"
                />
            </div>
        </div>
    </>
);

export const ZazehCompanyPage = memo(() => {
    const locale = useLocale();
    const { t } = useTranslation();

    const sidebar = <Sidebar hashtags={BLOG_ARTICLES.find((a) => a.key === "zazeh")?.hashtags} />;

    return (
        <main>
            <BlogHeroSection name={t("company:zazeh.name")} title={t("company:zazeh.title")}>
                <StaticImage
                    alt="Zazeh screen"
                    imgClassName="!w-full object-contain"
                    src={zazeh}
                    width={583}
                    widths={[400, 400, 583, 448, 540, 583]}
                />
            </BlogHeroSection>
            <MainSection className="mb-12 md:mb-20">
                <IntroSection sidebar={sidebar}>
                    <div className="ui-typo-body">{locale === "en" ? <ContentEn /> : <ContentCs />}</div>
                </IntroSection>
                <div className="mt-12 flex flex-col justify-between border-t pt-12 xl:hidden">{sidebar}</div>
            </MainSection>
        </main>
    );
});

ZazehCompanyPage.displayName = "ZazehPage";
